import { executionDecision, selectedCommandStatus, visitFullyExcluded, visitTimingStatus } from "./consolidationExecution.js";
import { isEarlier } from "./consolidationFieldHighlights.js";

// Keep the stored sequence (including skipped stops) for API compatibility.
// The editor exposes consecutive positions only for stops that can participate.
export function orderedActiveVisits(row, quantity) {
  return row.visits.filter(visit => visit.pm?.role !== "target" && !visitFullyExcluded(row, visit)
    && !(visitTimingStatus(row, visit, quantity) === "not_performed" && !visit.arrival && !visit.departure))
    .slice().sort((a, b) => a.actual_sequence - b.actual_sequence);
}

export function moveVisitToPosition(row, quantity, id, position) {
  const active = orderedActiveVisits(row, quantity);
  const slots = active.map(visit => visit.actual_sequence);
  const index = active.findIndex(visit => visit.id === id);
  const target = Number(position);
  if (index < 0 || !Number.isInteger(target) || target < 1 || target > active.length) return row.visits;
  const [moved] = active.splice(index, 1);
  const included = order => selectedCommandStatus(order, executionDecision(row, order)) !== "excluded";
  const orderIds = new Set(moved.orders.filter(included).map(order => order.id));
  let minimum = 0;
  let maximum = active.length;
  // Hidden PM evidence keeps its internal slot and must still precede delivery.
  row.visits.filter(visit => visit.pm?.role === "target").forEach(visit => {
    if (moved.kind === "delivery" && visit.orders.some(order => included(order) && orderIds.has(order.id))) {
      minimum = Math.max(minimum, slots.filter(sequence => sequence <= visit.actual_sequence).length);
    }
  });
  active.forEach((visit, position) => {
    if (!visit.orders.some(order => included(order) && orderIds.has(order.id))) return;
    if (moved.kind === "delivery" && visit.kind === "loading") minimum = Math.max(minimum, position + 1);
    if (moved.kind === "loading" && visit.kind === "delivery") maximum = Math.min(maximum, position);
  });
  const destination = Math.max(minimum, Math.min(maximum, target - 1));
  if (destination === index) return row.visits;
  active.splice(destination, 0, moved);
  const activeIds = new Set(active.map(visit => visit.id));
  let next = 0;
  const sequences = new Map(row.visits.slice().sort((a, b) => a.actual_sequence - b.actual_sequence)
    .map((visit, index) => [activeIds.has(visit.id) ? active[next++].id : visit.id, index + 1]));
  return row.visits.map(visit => ({ ...visit, actual_sequence: sequences.get(visit.id) }));
}

// Drivers choose their route: the departure time sets the real order. A stop moves up before a stop still
// waiting for its time that is planned later; a late stop never goes down below one. A preload done before
// the tour never moves, and equal times keep their place.
export function placeVisitByDeparture(row, quantity, id) {
  const active = orderedActiveVisits(row, quantity);
  const index = active.findIndex(visit => visit.id === id);
  const departure = active[index]?.departure;
  if (!departure) return row.visits;
  const time = visit => visit.departure || (visit.status !== "inherited" ? visit.planned_departure : null);
  const later = active.findIndex((visit, position) => position < index && isEarlier(departure, time(visit), row.timezone));
  const earlier = active.findLastIndex((visit, position) => position > index && isEarlier(visit.departure, departure, row.timezone));
  const target = later >= 0 ? later + 1 : earlier >= 0 ? earlier + 1 : null;
  let visits = target ? moveVisitToPosition(row, quantity, id, target) : row.visits;
  if (visits === row.visits || active[index].kind !== "loading") return visits;
  // A loading brings its deliveries still without a time: the trip stays together until their times say otherwise.
  const loaded = new Set(active[index].orders.map(order => order.id));
  const trip = visit => visit.kind === "delivery" && visit.orders.some(order => loaded.has(order.id));
  const order = orderedActiveVisits({ ...row, visits }, quantity);
  let next = order.findIndex(visit => visit.id === id) + 1;
  while (next < order.length && trip(order[next])) next += 1;
  for (const visit of order.slice(next).filter(visit => trip(visit) && !visit.departure)) {
    visits = moveVisitToPosition({ ...row, visits }, quantity, visit.id, next += 1);
  }
  // Its deliveries already timed then take the place of their own time.
  for (const visit of order.filter(visit => trip(visit) && visit.departure)) visits = placeVisitByDeparture({ ...row, visits }, quantity, visit.id);
  return visits;
}

// Remplissage depuis Geotab : tout le relevé suit les départs réels, d'un seul tri. Un arrêt sans heure garde sa place
// derrière l'arrêt qui le précède, mais un chargement remonte avant sa première livraison et une livraison ne passe
// jamais avant son chargement. Un préchargement ne bouge pas.
export function orderByDeparture(row, quantity) {
  const active = orderedActiveVisits(row, quantity);
  const shares = (visit, other) => visit.orders.some(order => other.orders.some(item => item.id === order.id));
  const timed = new Map(active.filter(visit => visit.departure && visit.status !== "inherited").map(visit => [visit.id, Date.parse(visit.departure)]));
  let previous = -Infinity;
  const entries = active.map((visit, index) => {
    let key = visit.status === "inherited" ? -Infinity : timed.get(visit.id);
    if (key == null) {
      const related = active.filter(other => timed.has(other.id) && other.kind !== visit.kind && shares(visit, other)).map(other => timed.get(other.id));
      key = !related.length ? previous
        : visit.kind === "loading" ? Math.min(previous, Math.min(...related) - 1) : Math.max(previous, Math.max(...related) + 1);
    }
    previous = key;
    return { id: visit.id, key, index };
  });
  entries.sort((a, b) => (a.key === b.key ? a.index - b.index : a.key - b.key));
  // Les contraintes habituelles (chargement avant livraison, preuve PM) restent celles du déplacement manuel.
  let visits = row.visits;
  entries.forEach(({ id }, position) => { visits = moveVisitToPosition({ ...row, visits }, quantity, id, position + 1); });
  return visits;
}
